/**
 * PrayerScheduleContext - Tanzeen+
 * ---------------------------------
 * Single source of truth for whichever prayer timetable the user is CURRENTLY
 * looking at on the /prayer-times page. Every "Next Prayer" widget across the
 * app (page headers, home cards, floating buttons, etc.) reads from this
 * context so they render EXACTLY the same times, whether the user is in
 * "CALCULATION" or "MOSQUE_EXTRACTION" mode.
 *
 * Design:
 *   * PrayerTrialPage calls `useSetActiveSchedule({ activeMode, activeTimetable })`
 *     whenever its `displayTimes` changes. That single hook write propagates
 *     to every consumer instantly (no reload, no polling).
 *   * We also mirror the state to `localStorage['tanzeen_active_prayer_times']`
 *     so a fresh page load can hydrate the context BEFORE PrayerTrialPage
 *     mounts (i.e., the header on /quran already shows the right time).
 *   * A `storage` event listener keeps cross-tab / cross-webview instances in
 *     sync as well.
 *
 * Shape of `activeTimetable` (matches PrayerTrialPage's `displayTimes`):
 *   {
 *     fajr:    { time, jamaah? },
 *     sunrise: { time },
 *     dhuhr:   { time, jamaah? },
 *     asr:     { time, jamaah? },
 *     maghrib: { time, jamaah? },
 *     isha:    { time, jamaah? },
 *     jumuah?: string | { time }
 *   }
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'tanzeen_active_prayer_times';

const PrayerScheduleContext = createContext(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const readInitial = () => {
  try {
    const raw = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const pickTime = (entry) => {
  if (!entry) return null;
  const raw = entry.time || entry.jamaah || entry.start;
  return raw ? String(raw).split(' ')[0] : null; // keep HH:MM head
};

const PRAYER_ORDER = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

const computeNextPrayer = (timetable) => {
  if (!timetable) return null;
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  for (const name of PRAYER_ORDER) {
    const t = pickTime(timetable[name]);
    if (!t) continue;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) continue;
    const prayerMin = h * 60 + m;
    if (prayerMin > nowMin) {
      return { name, time: t };
    }
  }
  // After Isha -> tomorrow's Fajr
  const fajr = pickTime(timetable.fajr);
  return fajr ? { name: 'fajr', time: fajr } : null;
};

const formatCountdown = (nextPrayer) => {
  if (!nextPrayer?.time) return '';
  const [h, m] = nextPrayer.time.split(':').map(Number);
  const now = new Date();
  const target = new Date();
  target.setHours(h, m, 0, 0);
  if (now > target) target.setDate(target.getDate() + 1);
  const diff = target - now;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const PrayerScheduleProvider = ({ children }) => {
  // Hydrate from localStorage synchronously so headers render correct on load
  const initial = readInitial();
  const [state, setState] = useState({
    activeMode: initial?.mode || null,       // 'MOSQUE_EXTRACTION' | 'CALCULATION' | null
    activeTimetable: initial?.prayers || null,
    lastUpdated: initial?.lastUpdated || null,
  });

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        setState({
          activeMode: parsed?.mode || null,
          activeTimetable: parsed?.prayers || null,
          lastUpdated: parsed?.lastUpdated || null,
        });
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Ticking clock -> re-derive nextPrayer / countdown once a minute
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const nextPrayer = useMemo(
    () => computeNextPrayer(state.activeTimetable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.activeTimetable, tick]
  );

  const formattedCountdown = useMemo(
    () => formatCountdown(nextPrayer),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nextPrayer, tick]
  );

  // Writer used by PrayerTrialPage
  const setActiveSchedule = ({ activeMode, activeTimetable }) => {
    const payload = {
      mode: activeMode,
      prayers: activeTimetable,
      lastUpdated: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
    setState({
      activeMode,
      activeTimetable,
      lastUpdated: payload.lastUpdated,
    });
  };

  const value = useMemo(
    () => ({
      activeMode: state.activeMode,
      activeTimetable: state.activeTimetable,
      lastUpdated: state.lastUpdated,
      nextPrayer,
      formattedCountdown,
      setActiveSchedule,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, nextPrayer, formattedCountdown]
  );

  return (
    <PrayerScheduleContext.Provider value={value}>
      {children}
    </PrayerScheduleContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const usePrayerSchedule = () => {
  const ctx = useContext(PrayerScheduleContext);
  if (!ctx) {
    // Safe default so components can render before Provider mounts (e.g. SSR)
    return {
      activeMode: null,
      activeTimetable: null,
      lastUpdated: null,
      nextPrayer: null,
      formattedCountdown: '',
      setActiveSchedule: () => {},
    };
  }
  return ctx;
};

export default PrayerScheduleContext;
